import type { ItemDto, Paginated } from '@bnn/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Card, Empty, Input, PageHeader, Pagination, Select, Spinner, Table, useDebounced } from '../components/ui';
import { qs } from '../lib/api';
import { useApi } from '../lib/useApi';

export default function ItemsPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = useDebounced(search.trim(), 300);
  const countable = params.get('countable') ?? 'true';
  const group = params.get('group') ?? '';
  const page = Number(params.get('page') ?? 1);

  const groups = useApi<{ item_group: string; count: number }[]>('/item-groups');
  const list = useApi<Paginated<ItemDto>>(`/items${qs({ search: q, countable: countable === 'all' ? undefined : countable, group, page, page_size: 50 })}`);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader title="สินค้า" subtitle="Master จาก SAP · ดูหน่วยที่นับได้และอัตราแปลงเป็น Inventory UoM" />
      <Card>
        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_180px_160px]">
          <Input
            type="search"
            placeholder="ค้นหารหัสหรือชื่อสินค้า (พิมพ์หลายคำได้)"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              set({ q: e.target.value });
            }}
            autoFocus
          />
          <Select value={countable} onChange={(e) => set({ countable: e.target.value })} aria-label="สถานะ">
            <option value="true">เฉพาะที่นับได้</option>
            <option value="false">นับไม่ได้ (ไม่ใช่สินค้าคงคลัง)</option>
            <option value="all">ทั้งหมด</option>
          </Select>
          <Select value={group} onChange={(e) => set({ group: e.target.value })} aria-label="กลุ่ม">
            <option value="">ทุกกลุ่ม</option>
            {groups.data?.map((g) => (
              <option key={g.item_group} value={g.item_group}>
                {g.item_group} ({g.count})
              </option>
            ))}
          </Select>
        </div>
        {list.error && <Alert>{list.error.message}</Alert>}
        {list.loading && !list.data && (
          <div className="py-8 text-center text-muted">
            <Spinner />
          </div>
        )}
        {list.data && list.data.total === 0 ? (
          <Empty>ไม่พบสินค้า</Empty>
        ) : (
          list.data && (
            <>
              <Table head={['รหัส', 'ชื่อสินค้า', 'กลุ่ม', 'Inventory UoM', '']}>
                {list.data.data.map((i) => (
                  <tr key={i.item_code} className="hover:bg-surface">
                    <td className="font-semibold whitespace-nowrap">
                      <Link to={`/items/${encodeURIComponent(i.item_code)}`} className="text-brand hover:underline">
                        {i.item_code}
                      </Link>
                    </td>
                    <td>{i.item_name}</td>
                    <td className="whitespace-nowrap">{i.item_group}</td>
                    <td className="whitespace-nowrap">{i.inventory_uom}</td>
                    <td>{!i.countable ? <Badge>ไม่ใช่สินค้าคงคลัง</Badge> : !i.is_active && <Badge tone="warn">Inactive ใน SAP</Badge>}</td>
                  </tr>
                ))}
              </Table>
              <Pagination page={list.data.page} pageSize={list.data.page_size} total={list.data.total} onPage={(p) => set({ page: String(p) })} />
            </>
          )
        )}
      </Card>
    </>
  );
}
